import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  fetchUsers, createUser, deleteUser, updateUser,
  getTrackingStatus, setTracking, getUserLocation, getUserLocationHistory,
  refreshUserLocation,
} from '../services/api';
import LocationMap from '../components/LocationMap';

const s = {
  page: { minHeight: '100vh', background: '#080b10' },
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 32px',
    borderBottom: '1px solid #1e2a36',
    background: '#0d1117',
    position: 'sticky', top: 0, zIndex: 100,
  },
  navBrand: {
    display: 'flex', alignItems: 'center', gap: '12px',
    fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: '800',
    color: '#e2eaf3',
  },
  navBadge: {
    fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
    background: 'rgba(0,229,255,0.1)', color: '#00e5ff',
    border: '1px solid rgba(0,229,255,0.2)',
    borderRadius: '4px', padding: '2px 8px', letterSpacing: '0.1em',
  },
  navLinks: { display: 'flex', gap: '16px', alignItems: 'center' },
  navLink: {
    fontSize: '12px', color: '#7a90a4', textDecoration: 'none',
    padding: '6px 12px', borderRadius: '6px',
    border: '1px solid transparent',
  },
  navLinkActive: {
    color: '#00e5ff', borderColor: 'rgba(0,229,255,0.2)',
    background: 'rgba(0,229,255,0.05)',
  },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '40px 32px' },
  sectionTitle: {
    fontFamily: 'Syne, sans-serif', fontSize: '22px', fontWeight: '700',
    color: '#e2eaf3', marginBottom: '24px',
  },
  createCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '28px', marginBottom: '40px',
  },
  createGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 140px auto', gap: '12px', alignItems: 'end' },
  fieldLabel: {
    fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase',
    color: '#7a90a4', marginBottom: '8px', display: 'block',
  },
  usersGrid: { display: 'grid', gap: '16px' },
  userCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '12px', padding: '20px 24px',
    display: 'grid', gridTemplateColumns: '1fr auto',
    alignItems: 'center', gap: '16px',
  },
  userName: { fontFamily: 'Syne, sans-serif', fontSize: '16px', fontWeight: '700', color: '#e2eaf3', marginBottom: '6px' },
  userMeta: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' },
  metaChip: {
    fontSize: '11px', fontFamily: 'JetBrains Mono, monospace',
    color: '#7a90a4', display: 'flex', alignItems: 'center', gap: '5px',
  },
  roleChip: {
    fontSize: '12px', fontFamily: 'JetBrains Mono, monospace',
    borderRadius: '6px', padding: '2px 10px', letterSpacing: '0.15em',
  },
  roleAdmin: { background: 'rgba(255,77,79,0.08)', color: '#ff4d4f', border: '1px solid rgba(255,77,79,0.15)' },
  roleOwner: { background: 'rgba(255,183,0,0.08)', color: '#ffb300', border: '1px solid rgba(255,183,0,0.15)' },
  roleUser: { background: 'rgba(0,230,118,0.08)', color: '#00e676', border: '1px solid rgba(0,230,118,0.15)' },
  actionRow: { display: 'flex', gap: '8px' },
  emptyState: { textAlign: 'center', padding: '60px 24px', color: '#3d5166', fontSize: '13px' },
  toast: {
    position: 'fixed', bottom: '24px', right: '24px',
    background: '#131920', border: '1px solid #00e676',
    borderRadius: '10px', padding: '12px 20px',
    color: '#00e676', fontSize: '13px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    zIndex: 1000,
  },
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200, backdropFilter: 'blur(4px)',
  },
  modalCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%',
  },
  modalTitle: { fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: '700', marginBottom: '12px' },
  modalSub: { color: '#7a90a4', fontSize: '13px', lineHeight: '1.6', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  trackBtn: {
    fontSize: '11px', borderRadius: '6px', padding: '4px 12px',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  trackOn: {
    background: 'rgba(0,230,118,0.08)', color: '#00e676',
    border: '1px solid rgba(0,230,118,0.2)',
  },
  trackOff: {
    background: 'rgba(122,144,164,0.08)', color: '#7a90a4',
    border: '1px solid rgba(122,144,164,0.2)',
  },
  locationModal: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '32px', maxWidth: '600px', width: '90%',
  },
  locationInfo: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px', flexWrap: 'wrap', gap: '12px',
  },
  locationText: { color: '#7a90a4', fontSize: '13px', fontFamily: 'JetBrains Mono, monospace' },
  noLocation: { textAlign: 'center', padding: '40px', color: '#3d5166', fontSize: '14px' },
};

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2500); return () => clearTimeout(t); }, [onDone]);
  return <div style={s.toast}>✓ {message}</div>;
}

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={s.modal} onClick={onCancel}>
      <div style={s.modalCard} onClick={e => e.stopPropagation()}>
        <div style={s.modalTitle}>Confirm Action</div>
        <div style={s.modalSub}>{message}</div>
        <div style={s.modalBtns}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

const roleStyle = (role) => {
  if (role === 'ADMIN') return { ...s.roleChip, ...s.roleAdmin };
  if (role === 'ROOM_OWNER') return { ...s.roleChip, ...s.roleOwner };
  return { ...s.roleChip, ...s.roleUser };
};

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('USER');
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [trackingMap, setTrackingMap] = useState({}); // userId -> boolean
  const [locationModal, setLocationModal] = useState(null); // { user, location, history }
  const [refreshing, setRefreshing] = useState(false);
  const [renameModal, setRenameModal] = useState(null); // { user, newName }
  const [renaming, setRenaming] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await fetchUsers();
      setUsers(data);
      // Load tracking status for each non-admin user.
      const statuses = {};
      await Promise.all(data.filter(u => u.role !== 'ADMIN').map(async u => {
        try {
          const { data: ts } = await getTrackingStatus(u.id);
          statuses[u.id] = ts.trackingEnabled;
        } catch { statuses[u.id] = false; }
      }));
      setTrackingMap(statuses);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername || !newPassword) return;
    setCreating(true);
    try {
      await createUser(newUsername, newPassword, newRole);
      setNewUsername('');
      setNewPassword('');
      setNewRole('USER');
      await loadUsers();
      setToast('User created successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (user) => {
    setConfirm({
      message: `Delete user "${user.username}"? This cannot be undone.`,
      action: async () => {
        await deleteUser(user.id);
        await loadUsers();
        setToast('User deleted');
      }
    });
  };

  const handleRename = async () => {
    if (!renameModal || !renameModal.newName?.trim()) return;
    setRenaming(true);
    try {
      await updateUser(renameModal.user.id, { username: renameModal.newName.trim() });
      await loadUsers();
      setToast('User renamed successfully');
      setRenameModal(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename user');
    } finally {
      setRenaming(false);
    }
  };

  // Toggle tracking for a user.
  const handleToggleTracking = async (user) => {
    const current = trackingMap[user.id] || false;
    const next = !current;
    try {
      await setTracking(user.id, next);
      setTrackingMap(prev => ({ ...prev, [user.id]: next }));
      setToast(`Tracking ${next ? 'enabled' : 'disabled'} for ${user.username}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle tracking');
    }
  };

  // Open the location modal for a user.
  const handleViewLocation = async (user) => {
    try {
      const [locRes, histRes] = await Promise.all([
        getUserLocation(user.id),
        getUserLocationHistory(user.id),
      ]);
      // If no location data, locRes.data has { message: "..." }
      const location = locRes.data.latitude ? locRes.data : null;
      const history = histRes.data || [];
      setLocationModal({ user, location, history });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load location');
    }
  };

  // Refresh: send FCM push to user's device, then poll for the
  // updated location every 2 seconds for up to 15 seconds.
  const handleRefreshLocation = async () => {
    if (!locationModal || refreshing) return;
    setRefreshing(true);
    try {
      // Send the location request push.
      await refreshUserLocation(locationModal.user.id);

      // Poll for updated location.
      let attempts = 0;
      const maxAttempts = 8; // 8 x 2s = 16s
      const poll = setInterval(async () => {
        attempts++;
        try {
          const [locRes, histRes] = await Promise.all([
            getUserLocation(locationModal.user.id),
            getUserLocationHistory(locationModal.user.id),
          ]);
          const newLocation = locRes.data.latitude ? locRes.data : null;
          const newHistory = histRes.data || [];

          // Check if we got a newer location.
          const oldTime = locationModal.location?.recordedAt;
          const newTime = newLocation?.recordedAt;
          const hasNew = newTime && (!oldTime || new Date(newTime) > new Date(oldTime));

          if (hasNew || attempts >= maxAttempts) {
            clearInterval(poll);
            setLocationModal(prev => ({ ...prev, location: newLocation, history: newHistory }));
            setRefreshing(false);
            if (!hasNew) {
              setToast('No new location received — make sure the app is running');
            } else {
              setToast('Location updated');
            }
          }
        } catch {
          if (attempts >= maxAttempts) {
            clearInterval(poll);
            setRefreshing(false);
          }
        }
      }, 2000);
    } catch (err) {
      setRefreshing(false);
      alert(err.response?.data?.error || 'Failed to request location');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <div style={s.navBrand}>
          <span>🔐</span>
          <span>SecretChat</span>
          <span style={s.navBadge}>ADMIN</span>
        </div>
        <div style={s.navLinks}>
          <Link to="/admin/dashboard" style={{ ...s.navLink }}>Rooms</Link>
          <Link to="/admin/users" style={{ ...s.navLink, ...s.navLinkActive }}>Users</Link>
          <button className="btn btn-ghost" onClick={handleLogout} style={{ fontSize: '12px' }}>
            Sign Out
          </button>
        </div>
      </nav>

      <div style={s.content}>
        <h2 style={s.sectionTitle}>Add New User</h2>
        <div style={s.createCard}>
          <form onSubmit={handleCreate}>
            <div style={s.createGrid}>
              <div>
                <label style={s.fieldLabel}>Username</label>
                <input
                  className="input"
                  value={newUsername}
                  onChange={e => setNewUsername(e.target.value)}
                  placeholder="e.g. john"
                  required
                />
              </div>
              <div>
                <label style={s.fieldLabel}>Password</label>
                <input
                  className="input"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="••••••"
                  required
                />
              </div>
              <div>
                <label style={s.fieldLabel}>Role</label>
                <select
                  className="input"
                  value={newRole}
                  onChange={e => setNewRole(e.target.value)}
                >
                  <option value="USER">User</option>
                  <option value="ROOM_OWNER">Room Owner</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creating || !newUsername || !newPassword}
                style={{ marginBottom: '22px', whiteSpace: 'nowrap' }}
              >
                {creating ? '...' : '+ Create'}
              </button>
            </div>
          </form>
        </div>

        <h2 style={s.sectionTitle}>
          All Users
          <span style={{ ...s.navBadge, marginLeft: '12px', verticalAlign: 'middle' }}>
            {users.length}
          </span>
        </h2>

        {loading ? (
          <div style={s.emptyState}>Loading users...</div>
        ) : users.length === 0 ? (
          <div style={s.emptyState}>No users yet. Create one above.</div>
        ) : (
          <div style={s.usersGrid}>
            {users.map(user => (
              <div key={user.id} style={s.userCard} className="fade-up">
                <div>
                  <div style={s.userName}>{user.username}</div>
                  <div style={s.userMeta}>
                    <span style={roleStyle(user.role)}>{user.role}</span>
                    {user.ownedRoomName && (
                      <span style={s.metaChip}>🏠 {user.ownedRoomName}</span>
                    )}
                    <span style={s.metaChip}>
                      📅 {new Date(user.createdAt).toLocaleDateString()}
                    </span>
                    <span style={s.metaChip}>
                      {user.active ? '✅ Active' : '⛔ Inactive'}
                    </span>
                  </div>
                </div>
                <div style={s.actionRow}>
                  {user.role !== 'ADMIN' && (
                    <>
                      <button
                        style={{
                          ...s.trackBtn,
                          ...(trackingMap[user.id] ? s.trackOn : s.trackOff),
                        }}
                        onClick={() => handleToggleTracking(user)}
                      >
                        {trackingMap[user.id] ? '📍 Tracking On' : '📍 Track'}
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setRenameModal({ user, newName: user.username })}
                        style={{ fontSize: '11px' }}
                      >
                        ✎ Rename
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => handleViewLocation(user)}
                        style={{ fontSize: '11px' }}
                      >
                        🗺 View
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(user)}
                        style={{ fontSize: '11px' }}
                      >
                        ✕ Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && <Toast message={toast} onDone={() => setToast('')} />}
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={async () => {
            await confirm.action();
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {renameModal && (
        <div style={s.modal} onClick={() => setRenameModal(null)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Rename User</div>
            <div style={s.modalSub}>
              Enter a new username for <b>{renameModal.user.username}</b>
            </div>
            <input
              className="input"
              value={renameModal.newName}
              onChange={e => setRenameModal(prev => ({ ...prev, newName: e.target.value }))}
              placeholder="New username"
              style={{ width: '100%', marginBottom: '20px' }}
              autoFocus
            />
            <div style={s.modalBtns}>
              <button className="btn btn-ghost" onClick={() => setRenameModal(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleRename}
                disabled={renaming || !renameModal.newName?.trim() || renameModal.newName === renameModal.user.username}
              >
                {renaming ? '...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {locationModal && (
        <div style={s.modal} onClick={() => setLocationModal(null)}>
          <div style={s.locationModal} onClick={e => e.stopPropagation()}>
            <div style={{ ...s.modalTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📍 {locationModal.user.username}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-ghost"
                  onClick={handleRefreshLocation}
                  disabled={refreshing}
                  style={{ fontSize: '11px' }}
                >
                  {refreshing ? '⏳ Requesting...' : '🔄 Refresh'}
                </button>
                <button className="btn btn-ghost" onClick={() => setLocationModal(null)} style={{ fontSize: '11px' }}>✕</button>
              </div>
            </div>
            {locationModal.location ? (
              <>
                <div style={s.locationInfo}>
                  <span style={s.locationText}>
                    Lat: {locationModal.location.latitude.toFixed(6)},{' '}
                    Lng: {locationModal.location.longitude.toFixed(6)}
                  </span>
                  <span style={s.locationText}>
                    Updated: {new Date(locationModal.location.recordedAt).toLocaleString()}
                  </span>
                </div>
                <LocationMap
                  latitude={locationModal.location.latitude}
                  longitude={locationModal.location.longitude}
                  history={locationModal.history}
                  username={locationModal.user.username}
                />
                <div style={{ ...s.locationText, marginTop: '12px' }}>
                  {locationModal.history.length} location pings recorded
                </div>
              </>
            ) : (
              <div style={s.noLocation}>
                No location data available yet.
                <br />
                Make sure tracking is enabled and the user's app is running.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
